import { TextField, PhoneField, CountryCodeItem, H3 } from '@wheely/ui-kit';
import { AUTH_ERROR_CODES, OTP_TRANSPORTS, type OtpTransport } from '@wheely/web-auth-lib/core';
import {
  REGISTRATION_PHASES,
  useRegistrationFlow,
  type OtpChallengeController,
  type PhoneConflictHandoff,
} from '@wheely/web-auth-lib/react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PHONE_CODES } from '@/shared/const/phone-codes';
import { useDismissibleError } from '@/shared/hooks/useDismissibleError';
import { getAuthErrorText } from '@/shared/utils/getAuthErrorText';
import { getInitialPhoneCodeByLocale } from '@/shared/utils/getInitialPhoneCodeByLocale';
import { normalizePhoneNumber } from '@/shared/utils/phoneUtils';
import { ChangeButton } from '@/shared/view/ChangeButton';
import { CodeForm } from '@/shared/view/CodeForm';
import { LinkButton } from '@/shared/view/LinkButton';
import { Button } from '@src-common/view/adapters/Button';

import { Captcha, CaptchaHandle } from '../Captcha/Captcha';
import { PhoneHandoff } from '../PhoneHandoff';

import s from './style.module.css';

type Props = {
  initialEmail?: string;
  /** The email already has an account: the parent switches to sign-in. */
  onSignIn: (email: string) => void;
};

/** The b2b wording of an OTP channel; an unknown one falls back to its name. */
const transportLabel = (transport: OtpTransport): React.ReactNode => {
  switch (transport) {
    case OTP_TRANSPORTS.SMS:
      return (
        <FormattedMessage
          defaultMessage="SMS"
          id="login_transport_sms"
          description="SMS transport"
        />
      );
    case OTP_TRANSPORTS.WHATSAPP:
      return (
        <FormattedMessage
          defaultMessage="WhatsApp"
          id="login_transport_whatsapp"
          description="WhatsApp transport"
        />
      );
    case OTP_TRANSPORTS.AUTO_CALL:
      return (
        <FormattedMessage
          defaultMessage="Call me"
          id="login_transport_call"
          description="Call transport"
        />
      );
    case OTP_TRANSPORTS.TELEGRAM:
      return (
        <FormattedMessage
          defaultMessage="Telegram"
          id="login_transport_telegram"
          description="Telegram transport"
        />
      );
    default:
      return transport;
  }
};

/**
 * Two-phase registration: the email is confirmed by a code from the letter,
 * then the phone by an SMS code. The code entry appears under the field that
 * requested it, which stays visible but disabled. The server owns the order;
 * paid transports go through the captcha. A phone that already has an account
 * hands the flow over to a phone sign-in that links the verified email.
 */
const RegistrationForm: React.VFC<Props> = ({ initialEmail = '', onSignIn }) => {
  const intl = useIntl();
  const [email, setEmail] = useState(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState(getInitialPhoneCodeByLocale(intl.locale));
  const [phoneValidationError, setPhoneValidationError] = useState<string | undefined>(undefined);
  const [handoff, setHandoff] = useState<OtpChallengeController | undefined>(undefined);
  const [phoneConflict, setPhoneConflict] = useState<PhoneConflictHandoff | undefined>(undefined);
  const captchaRef = useRef<CaptchaHandle>(null);
  // The flow keeps its error until the next submitIdentifier(), so the line under
  // the identifier field outlives the value it described. Editing that field drops
  // it; the code step has its own field and clears its own error.
  const { isDismissed, dismiss, restore } = useDismissibleError();

  const registration = useRegistrationFlow({
    // Paid transports only: the email phase never asks for a captcha.
    getCaptchaToken: () => captchaRef.current?.getCaptcha() ?? Promise.resolve(undefined),
    // Kept passive: the sign-in by the busy phone starts on the user's click.
    onPhoneConflict: setPhoneConflict,
  });

  const { phase } = registration;
  // A busy identifier is not a failure: an inline error offers the sign-in and
  // the switch stays the user's decision, as on the b2b card.
  const isEmailConflict = registration.error?.code === AUTH_ERROR_CODES.EMAIL_IDENTIFIER_CONFLICT;
  const isPhoneConflict = registration.error?.code === AUTH_ERROR_CODES.PHONE_IDENTIFIER_CONFLICT;
  const errorText =
    registration.error && !isEmailConflict && !isPhoneConflict
      ? getAuthErrorText(intl, registration.error)
      : undefined;
  const identifierErrorText = isDismissed ? undefined : errorText;
  const showEmailConflict = isEmailConflict && !isDismissed;
  const showPhoneConflict = isPhoneConflict && !isDismissed;

  // The handoff signs in by the busy phone and attaches the verified email.
  const handleConflictSignIn = useCallback(() => {
    phoneConflict?.startPhoneLogin().then(setHandoff);
  }, [phoneConflict]);

  // A hand-over from the sign-in already carries the email: start right away,
  // so the user lands on the code entry instead of typing the email again.
  const autoStartedRef = useRef(false);
  const [isAutoStarting, setIsAutoStarting] = useState(initialEmail !== '');

  useEffect(() => {
    if (!autoStartedRef.current && initialEmail) {
      autoStartedRef.current = true;
      registration.submitIdentifier(initialEmail).finally(() => setIsAutoStarting(false));
    }
  }, [initialEmail, registration]);

  const handleSubmitIdentifier = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      restore();

      if (phase === REGISTRATION_PHASES.EMAIL) {
        registration.submitIdentifier(email);

        return;
      }

      // The code is paid: an obviously wrong number never leaves the browser.
      const phone = normalizePhoneNumber(countryCode + phoneNumber);

      if (!phone) {
        setPhoneValidationError(
          intl.formatMessage({
            defaultMessage: 'Please enter a valid phone number.',
            id: 'login_error_invalid_phone',
            description: 'Phone validation error',
          }),
        );

        return;
      }

      setPhoneValidationError(undefined);
      registration.submitIdentifier(phone);
    },
    [phase, email, countryCode, phoneNumber, registration, intl, restore],
  );

  const handleChangeCode = useCallback(
    (code: CountryCodeItem) => {
      setCountryCode(code.value);
      setPhoneValidationError(undefined);
      dismiss();
    },
    [dismiss],
  );

  const handleChangePhone = useCallback(
    (next: string) => {
      setPhoneNumber(next);
      setPhoneValidationError(undefined);
      dismiss();
    },
    [dismiss],
  );

  const handleChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      dismiss();
    },
    [dismiss],
  );

  if (handoff) {
    // The note names the number in the form the identifier went out as, not the
    // raw field: a national leading zero never reached the API either.
    return (
      <PhoneHandoff
        controller={handoff}
        phone={normalizePhoneNumber(countryCode + phoneNumber) ?? countryCode + phoneNumber}
      />
    );
  }

  const isEmailStep =
    phase === REGISTRATION_PHASES.EMAIL || phase === REGISTRATION_PHASES.EMAIL_OTP;
  const isPhoneStep =
    phase === REGISTRATION_PHASES.PHONE ||
    phase === REGISTRATION_PHASES.PHONE_TRANSPORT ||
    phase === REGISTRATION_PHASES.PHONE_OTP;
  const isEmailEditable = phase === REGISTRATION_PHASES.EMAIL && !isAutoStarting;
  const isPhoneEditable = phase === REGISTRATION_PHASES.PHONE;

  return (
    <div className={s.registrationForm} data-test-id="registration-form">
      <Captcha ref={captchaRef} />

      {isEmailStep && (
        <>
          {phase === REGISTRATION_PHASES.EMAIL_OTP && (
            <H3>
              <FormattedMessage
                defaultMessage="Verify Your Email"
                id="login_registration_verify_email_title"
                description="Registration email code step title"
              />
            </H3>
          )}
          <form
            onSubmit={handleSubmitIdentifier}
            data-test-id="registration-form__email"
            noValidate={true}
          >
            <div className={s.field}>
              <TextField
                type="email"
                value={email}
                title={
                  <FormattedMessage
                    defaultMessage="Email"
                    id="login_email_title"
                    description="Email field label"
                  />
                }
                description={
                  isEmailEditable ? (
                    <FormattedMessage
                      description="Registration email field label"
                      defaultMessage="We will send a code to this email to confirm it."
                      id="login_registration_email_description"
                    />
                  ) : undefined
                }
                onChange={handleChangeEmail}
                placeholder={intl.formatMessage({
                  description: 'Email field placeholder',
                  defaultMessage: 'Email',
                  id: 'login_email_placeholder',
                })}
                disabled={!isEmailEditable}
                error={
                  showEmailConflict ? (
                    <span data-test-id="registration-form__email-conflict">
                      <FormattedMessage
                        defaultMessage="Account already exists."
                        id="login_registration_email_conflict"
                        description="Busy email error"
                      />{' '}
                      <LinkButton onClick={() => onSignIn(email)}>
                        <FormattedMessage
                          defaultMessage="Sign In"
                          id="login_registration_email_conflict_sign_in"
                          description="Busy email sign-in link"
                        />
                      </LinkButton>
                    </span>
                  ) : isEmailEditable ? (
                    identifierErrorText
                  ) : undefined
                }
                autoFocus={isEmailEditable}
              />
              {phase === REGISTRATION_PHASES.EMAIL_OTP && (
                <ChangeButton onClick={registration.reset}>
                  <FormattedMessage
                    defaultMessage="Change Email"
                    id="login_change_email"
                    description="Change email action"
                  />
                </ChangeButton>
              )}
            </div>
            {isEmailEditable && (
              <div className={s.submit}>
                <Button isDisabled={!email} type="submit">
                  <FormattedMessage
                    defaultMessage="Continue"
                    id="global_continue"
                    description="Continue button title"
                  />
                </Button>
              </div>
            )}
            {isAutoStarting && (
              <p data-test-id="registration-form__pending">
                <FormattedMessage
                  defaultMessage="Sending the code…"
                  id="login_registration_sending_code"
                  description="Auto-start pending note"
                />
              </p>
            )}
          </form>
          {phase === REGISTRATION_PHASES.EMAIL_OTP && (
            <CodeForm
              description={
                <FormattedMessage
                  defaultMessage="Verify your email address by entering one-time code sent to {email}"
                  id="login_registration_verify_email_description"
                  description="Registration email code step lead"
                  values={{ email: <b>{email}</b> }}
                />
              }
              disabled={false}
              error={errorText}
              attemptsLeft={registration.attemptsLeft}
              resendAvailableIn={registration.resendAvailableIn}
              onSubmit={code => registration.submitCode(code)}
              onResend={() => registration.resend(registration.transports?.preferredTransport)}
            />
          )}
        </>
      )}

      {isPhoneStep && (
        <>
          <H3>
            <FormattedMessage
              defaultMessage="Add Phone Number"
              id="login_registration_phone_title"
              description="Registration phone step title"
            />
          </H3>
          <form
            onSubmit={handleSubmitIdentifier}
            data-test-id="registration-form__phone"
            noValidate={true}
          >
            <div className={s.field}>
              <PhoneField
                autoFocus={isPhoneEditable}
                value={phoneNumber}
                title={
                  <FormattedMessage
                    defaultMessage="Phone Number"
                    id="login_phone_title"
                    description="Phone field label"
                  />
                }
                description={
                  isPhoneEditable ? (
                    <FormattedMessage
                      description="Registration phone step lead"
                      defaultMessage="This helps keep your account secure. We will send one-time code to your phone number"
                      id="login_registration_phone_description"
                    />
                  ) : undefined
                }
                countryCode={countryCode}
                onChange={handleChangePhone}
                items={PHONE_CODES}
                placeholder={intl.formatMessage({
                  description: 'Phone field placeholder',
                  defaultMessage: 'Phone number',
                  id: 'pTNKg/',
                })}
                onCodeChange={handleChangeCode}
                disabled={!isPhoneEditable}
                error={
                  isPhoneEditable ? (
                    showPhoneConflict ? (
                      <span data-test-id="registration-form__phone-conflict">
                        <FormattedMessage
                          defaultMessage="This phone number is already registered."
                          id="login_registration_phone_conflict"
                          description="Busy phone error"
                        />{' '}
                        <LinkButton onClick={handleConflictSignIn}>
                          <FormattedMessage
                            defaultMessage="Sign in"
                            id="login_registration_phone_conflict_sign_in"
                            description="Busy phone sign-in link"
                          />
                        </LinkButton>
                      </span>
                    ) : (
                      (phoneValidationError ?? identifierErrorText)
                    )
                  ) : undefined
                }
                selectProps={{
                  buttonProps: { 'data-test-id': 'registration-form__country-code' },
                }}
              />
              {!isPhoneEditable && (
                <ChangeButton onClick={() => registration.changePhone()}>
                  <FormattedMessage
                    defaultMessage="Change Phone Number"
                    id="login_change_phone"
                    description="Change phone action"
                  />
                </ChangeButton>
              )}
            </div>
            {isPhoneEditable && (
              <div className={s.submit}>
                <Button isDisabled={!phoneNumber} type="submit">
                  <FormattedMessage
                    defaultMessage="Continue"
                    id="global_continue"
                    description="Continue button title"
                  />
                </Button>
              </div>
            )}
          </form>
          {phase === REGISTRATION_PHASES.PHONE_TRANSPORT && (
            <div>
              <p>
                <FormattedMessage
                  defaultMessage="Choose how to receive the code:"
                  id="login_registration_transport_choice"
                  description="Transport choice note"
                />
              </p>
              <div className={s.transports}>
                {registration.transports?.allowedTransports.map(transport => (
                  <Button
                    key={transport}
                    onClick={() => registration.send(transport)}
                    type="button"
                  >
                    {transportLabel(transport)}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {phase === REGISTRATION_PHASES.PHONE_OTP && (
            <CodeForm
              description={
                <FormattedMessage
                  defaultMessage="Enter the code we sent to {phone}."
                  id="login_registration_phone_code_description"
                  description="Registration phone code field label"
                  values={{ phone: <b>{countryCode + phoneNumber}</b> }}
                />
              }
              disabled={false}
              error={errorText}
              attemptsLeft={registration.attemptsLeft}
              resendAvailableIn={registration.resendAvailableIn}
              onSubmit={code => registration.submitCode(code)}
              // Named, not defaulted: without a transport the flow repeats the
              // last one it used, so a single Call me would turn every later
              // Resend into another call.
              onResend={() => registration.resend(registration.transports?.preferredTransport)}
              onCallMe={
                registration.transports?.allowedTransports.includes(OTP_TRANSPORTS.AUTO_CALL)
                  ? () => registration.resend(OTP_TRANSPORTS.AUTO_CALL)
                  : undefined
              }
            />
          )}
        </>
      )}

      {phase === REGISTRATION_PHASES.FAILED && (
        <div data-test-id="registration-form__failed">
          <p>{errorText}</p>
          <Button onClick={registration.reset} type="button">
            <FormattedMessage
              defaultMessage="Start over"
              id="login_start_over"
              description="Start over button title"
            />
          </Button>
        </div>
      )}
    </div>
  );
};

export { RegistrationForm };
